/* Modal player for atd-youtube-shorts — with scrollbar compensation */
(() => {
    const SEL_SECTION = '[data-carousel][data-use-modal="true"]';
    const SEL_CARD = '.atd-ys-card';
    const SEL_CARD_BTN = '[data-play]';
    const MODAL_ID = 'atd-ys-modal-root';
    const ANIM_MS = 180;

    const buildEmbedUrl = (id) =>
        `https://www.youtube.com/embed/${id}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;

    const getIdFromCard = (card) => {
        if (!card) return '';
        const dataId = card.getAttribute('data-ytid') || '';
        if (dataId) return dataId.trim();

        // fallback: try data-href
        const href = card.getAttribute('data-href') || '';
        if (!href) return '';
        try {
            const u = new URL(href, location.origin);
            if (u.hostname.includes('youtube.') && u.pathname.startsWith('/shorts/')) {
                return (u.pathname.split('/')[2] || '').trim();
            }
            return (u.searchParams.get('v') || '').trim();
        } catch {
            return href.trim();
        }
    };

    /* --- Scrollbar compensation (reliable) --- */
    const state = {
        locked: false,
        prev: { overflow: '', padRight: '' },
        sbw: 0
    };

    function getScrollbarWidth() {
        // Actual gutter: viewport width minus layout width
        const sbw = window.innerWidth - document.documentElement.clientWidth;
        return sbw > 0 ? sbw : 0;
    }

    function lockScrollWithCompensation() {
        if (state.locked) return;

        const scrollEl = document.scrollingElement || document.documentElement; // the real scroller
        const html = document.documentElement;

        state.sbw = getScrollbarWidth();

        // remember prior inline styles to restore exactly
        state.prev.overflow = scrollEl.style.overflow;
        state.prev.padRight = scrollEl.style.paddingRight;

        // compute numeric padding-right currently applied (from computed style)
        const currentPad = parseFloat(getComputedStyle(scrollEl).paddingRight || '0') || 0;

        if (state.sbw > 0) {
            scrollEl.style.paddingRight = (currentPad + state.sbw) + 'px';
            html.style.setProperty('--atd-sbw', state.sbw + 'px'); // optional CSS var
        }

        scrollEl.style.overflow = 'hidden';
        html.classList.add('atd-ys-modal--open'); // keep your class hook if you use it elsewhere

        // If you have a fixed header, mark it with [data-avoid-shift] to compensate too:
        document.querySelectorAll('[data-avoid-shift]').forEach((el) => {
            const prev = parseFloat(getComputedStyle(el).paddingRight || '0') || 0;
            el.dataset.prevPadRight = el.style.paddingRight || '';
            if (state.sbw > 0) el.style.paddingRight = (prev + state.sbw) + 'px';
        });

        state.locked = true;
    }

    function unlockScrollWithCompensation() {
        if (!state.locked) return;

        const scrollEl = document.scrollingElement || document.documentElement;
        const html = document.documentElement;

        // restore exactly
        scrollEl.style.overflow = state.prev.overflow || '';
        scrollEl.style.paddingRight = state.prev.padRight || '';
        html.style.removeProperty('--atd-sbw');
        html.classList.remove('atd-ys-modal--open');

        document.querySelectorAll('[data-avoid-shift]').forEach((el) => {
            el.style.paddingRight = el.dataset.prevPadRight || '';
            delete el.dataset.prevPadRight;
        });

        state.locked = false;
    }


    const ensureModal = () => {
        let root = document.getElementById(MODAL_ID);
        if (root) return root;

        root = document.createElement('div');
        root.id = MODAL_ID;
        root.innerHTML = `
      <div class="atd-ys-modal__overlay" data-ys-overlay></div>
      <div class="atd-ys-modal__shell" data-ys-shell aria-live="polite" aria-atomic="true">
        <div class="atd-ys-modal__dialog" role="dialog" aria-modal="true" aria-label="YouTube video" data-ys-dialog>
          <button class="atd-ys-modal__close" type="button" data-ys-close aria-label="Close">✕</button>
          <iframe class="atd-ys-modal__iframe" data-ys-iframe allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>
        </div>
      </div>
    `;
        document.body.appendChild(root);

        // Close handlers
        const overlay = root.querySelector('[data-ys-overlay]');
        const closeBtn = root.querySelector('[data-ys-close]');
        const onClose = () => closeModal(root);
        overlay.addEventListener('click', onClose);
        closeBtn.addEventListener('click', onClose);
        document.addEventListener('keydown', (e) => {
            if (root.dataset.open === 'true' && e.key === 'Escape') onClose();
        });

        return root;
    };

    const openModal = (videoId) => {
        const root = ensureModal();
        const shell = root.querySelector('[data-ys-shell]');
        const overlay = root.querySelector('[data-ys-overlay]');
        const dialog = root.querySelector('[data-ys-dialog]');
        const iframe = root.querySelector('[data-ys-iframe]');

        root.dataset.open = 'true';
        lockScrollWithCompensation();   // <— JS approach added

        // Make visible first (so iframe has size), then set src
        shell.classList.add('is-open');
        overlay.classList.add('is-open');

        requestAnimationFrame(() => {
            overlay.classList.add('is-visible');
            dialog.classList.add('is-visible');
            iframe.src = buildEmbedUrl(videoId);
        });
    };

    const closeModal = (root) => {
        const shell = root.querySelector('[data-ys-shell]');
        const overlay = root.querySelector('[data-ys-overlay]');
        const dialog = root.querySelector('[data-ys-dialog]');
        const iframe = root.querySelector('[data-ys-iframe]');

        root.dataset.open = 'false';

        // stop video immediately
        iframe.src = '';

        overlay.classList.remove('is-visible');
        dialog.classList.remove('is-visible');

        setTimeout(() => {
            overlay.classList.remove('is-open');
            shell.classList.remove('is-open');
            unlockScrollWithCompensation();  // <— JS approach added
        }, ANIM_MS);
    };

    // Prevent the “open in new tab” from your carousel script when modal is enabled
    const swallowCardClicks = (section) => {
        section.addEventListener('click', (e) => {
            const card = e.target.closest(SEL_CARD);
            if (!card) return;

            // only if this section opted into modal
            if (!section.matches(SEL_SECTION)) return;

            if (e.target.closest(SEL_CARD_BTN) || card) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                const id = getIdFromCard(card);
                if (id) openModal(id);
            }
        }, true); // capture phase: cancel other handlers
    };

    const init = () => {
        document.querySelectorAll(SEL_SECTION).forEach(swallowCardClicks);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
